import type { Initiative } from '../../../../api/initiative-client/initiative-client.type';
import { InitiativeStatus } from '../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../api/projects-client/projects-client.type';
import { formatInitiativeDate } from '../../strategy-v2.helpers';
import { getContributingTeams } from '../main-section/main-section.helpers';
import { ChangeLogStatus } from './side-section.type';

/**
 * Generates an array of label-value pairs containing initiative information
 *
 * @param initiative - The initiative object containing the data
 * @returns An array of objects with label and value properties representing initiative details
 *          Each object contains:
 *          - label: The display label for the field
 *          - value: The corresponding value from the initiative object
 */
const getInitiativeInfo = (
  initiative: Initiative,
  teams: Team[],
): { label: string; value: string | number | null | undefined }[] => {
  return [
    {
      label: 'Start Date',
      value: formatInitiativeDate(initiative.start_date),
    },
    { label: 'Target End', value: formatInitiativeDate(initiative.end_date) },
    {
      label: 'Activity Start Date',
      value:
        initiative.effective_start_date && initiative.effective_start_date !== initiative.start_date
          ? formatInitiativeDate(initiative.effective_start_date)
          : 'Same as Start Date',
    },
    {
      label: 'Activity End Date',
      value:
        initiative.effective_end_date && initiative.effective_end_date !== initiative.end_date
          ? formatInitiativeDate(initiative.effective_end_date)
          : 'Same as End Date',
    },
    { label: 'Status (By Points)', value: getInitiativeStatus(initiative.status?.by_points?.status) || '-' },
    { label: 'Status (By Tasks)', value: getInitiativeStatus(initiative.status?.by_tasks?.status) || '-' },
    { label: 'Cost to Date', value: `$${initiative.costs_to_date?.initiative || 0}` },
    { label: 'Business Value', value: initiative.relative_business_value ?? '-' },
    { label: 'Teams', value: getContributingTeams(initiative, teams) || '-' },
  ];
};

/**
 * Converts a Status enum value to its corresponding display text
 *
 * @param {ChangeLogStatus} status - The Status enum value to convert
 * @returns {string} The human-readable text representation of the status
 */
const getStatusText = (status: ChangeLogStatus): string => {
  const labelMapping = {
    [ChangeLogStatus.Created]: 'Created',
    [ChangeLogStatus.InProgress]: 'In-Progress',
    [ChangeLogStatus.NotActive]: 'Not Active',
    [ChangeLogStatus.NotStarted]: 'Not Started',
    [ChangeLogStatus.Completed]: 'Completed',
  };

  if (!labelMapping[status]) {
    return 'Unknown';
  }

  return labelMapping[status];
};

/**
 * Helper method to get the string of the initiative status
 *
 * @param {InitiativeStatus| undefined} status enum choice of the initiative status
 * @returns {string} the label string of the status
 */
const getInitiativeStatus = (status: InitiativeStatus | undefined): string => {
  const labelMapping = {
    [InitiativeStatus.AtRisk]: 'At Risk',
    [InitiativeStatus.OffTrack]: 'Off Track',
    [InitiativeStatus.OnTrack]: 'On Track',
    [InitiativeStatus.Unknown]: 'Unknown',
    [InitiativeStatus.Empty]: 'Empty',
  };

  return status ? labelMapping[status] : 'Unknown';
};

export { getInitiativeInfo, getInitiativeStatus, getStatusText };
